// src/utils/adminEntitlements.js
import { auth } from '../firebase';
import { getApiEndpoint } from './apiBaseUrl';

/**
 * @param {Response} response
 * @returns {Promise<object>}
 */
async function parseJsonResponse(response) {
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const err = new Error(
      response.status >= 500
        ? 'Server error. Please try again.'
        : `Unexpected response (${response.status}).`
    );
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * @returns {Promise<string>}
 */
async function getBearerToken() {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('Authentication required. Please sign in again.');
    err.code = 'MISSING_AUTH';
    throw err;
  }
  return user.getIdToken();
}

/**
 * @param {Response} response
 * @param {object} data
 */
function throwAdminApiError(response, data) {
  const err = new Error(data.error || `Request failed (${response.status}).`);
  err.code = data.code;
  err.status = response.status;
  throw err;
}

/**
 * @param {string} path
 * @param {RequestInit} [options]
 */
async function adminFetch(path, options = {}) {
  const token = await getBearerToken();
  const response = await fetch(getApiEndpoint(path), {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throwAdminApiError(response, data);
  }
  return data;
}

export const OPERATOR_ERROR_MESSAGES = {
  OPERATOR_LOOKUP_REQUIRED:
    'Your account does not have operator access (support_admin or super_admin). Ask an engineer to grant claims, then refresh.',
  SUPER_ADMIN_REQUIRED:
    'Super-admin access is required for this action. Refresh your session after claim changes.',
  AUTH_USER_NOT_FOUND: 'No Firebase Auth user found for that email.',
  FEATURE_DISABLED: 'Enhancement is globally disabled (kill switch). Entitlement data may still be shown.',
  INVALID_QUERY: 'Invalid search or filter parameters.',
  ENTITLEMENT_LIST_FAILED: 'Could not load entitlement list. Try again.',
  ENTITLEMENT_AUDIT_FAILED: 'Could not load audit history. Try again.',
  ENTITLEMENT_CHECK_FAILED: 'Could not load entitlement summary. Try again.',
};

/**
 * @param {{ filter?: string, limit?: number, cursor?: string|null }} params
 */
export async function fetchEntitlementList(params = {}) {
  const { filter = 'all', limit = 25, cursor = null } = params;
  const qs = new URLSearchParams({ filter, limit: String(limit) });
  if (cursor) qs.set('cursor', cursor);
  return adminFetch(`/admin/entitlement-list?${qs.toString()}`);
}

/**
 * @param {{ targetUid?: string, email?: string }} params
 */
export async function fetchEntitlementLookup(params) {
  const qs = new URLSearchParams();
  if (params.targetUid) qs.set('targetUid', params.targetUid);
  if (params.email) qs.set('email', params.email);
  return adminFetch(`/admin/entitlement-lookup?${qs.toString()}`);
}

/**
 * @param {{ targetUid: string, limit?: number }} params
 */
export async function fetchEntitlementAudit(params) {
  const qs = new URLSearchParams({ targetUid: params.targetUid });
  if (params.limit != null) qs.set('limit', String(params.limit));
  return adminFetch(`/admin/entitlement-audit?${qs.toString()}`);
}

/**
 * @param {object} body US-06 override body
 */
export async function postEntitlementOverride(body) {
  const token = await getBearerToken();
  const response = await fetch(getApiEndpoint('/admin/entitlement-override'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throwAdminApiError(response, data);
  }
  return data;
}

export function operatorErrorMessage(error) {
  if (!error) return 'Something went wrong.';
  if (error.code && OPERATOR_ERROR_MESSAGES[error.code]) {
    return OPERATOR_ERROR_MESSAGES[error.code];
  }
  return error.message || 'Something went wrong.';
}
